<template>
  <div class="chat-page">
    <!-- 顶部提示信息 -->
    <div class="page-header">
      <h2>消息中心</h2>
      <p>会话数: {{ conversations.length }} | 加载中: {{ loadingConversations ? '是' : '否' }}</p>
    </div>

    <div class="chat-layout">
      <!-- 左侧：会话列表 -->
      <div class="conversations-sidebar">
        <div class="sidebar-header">
          <h3>消息列表</h3>
          <el-badge :value="totalUnreadCount" :max="99" :hidden="totalUnreadCount === 0">
            <el-icon :size="20"><ChatDotRound /></el-icon>
          </el-badge>
        </div>
        
        <!-- 搜索框 -->
        <div class="search-box">
          <el-input 
            v-model="conversationSearch" 
            placeholder="搜索企业名称"
            :prefix-icon="Search"
            clearable
            @input="loadConversations"
          />
        </div>

        <!-- 会话列表 -->
        <div class="conversations-list">
          <div v-if="loadingConversations" class="loading-state">
            <el-icon class="is-loading"><Loading /></el-icon>
            <span>加载中...</span>
          </div>
          
          <div 
            v-for="conv in conversations" 
            :key="conv.conversationId"
            :class="['conversation-item', { active: currentConversation?.conversationId === conv.conversationId }]"
            @click="selectConversation(conv)"
          >
            <el-badge :value="conv.unreadCount" :hidden="conv.unreadCount === 0" :max="99">
              <el-avatar :size="48" :src="getAvatarUrl(conv.otherUser.avatar)">
                {{ conv.otherUser.companyName?.substring(0, 1) || conv.otherUser.username?.substring(0, 1) || '企' }}
              </el-avatar>
            </el-badge>
            
            <div class="conv-info">
              <div class="conv-header">
                <span class="conv-name">{{ conv.otherUser.companyName || conv.otherUser.username }}</span>
                <span class="conv-time">{{ formatTime(conv.updatedAt) }}</span>
              </div>
              <div class="conv-last-message">
                {{ formatLastMessage(conv.lastMessage) }}
              </div>
            </div>
          </div>
          
          <el-empty 
            v-if="!loadingConversations && conversations.length === 0" 
            description='暂无消息，点击岗位详情页的"联系企业"按钮开始聊天' 
            :image-size="100" />
        </div>
      </div>

      <!-- 右侧：聊天区域 -->
      <div class="chat-area">
        <!-- 聊天头部 -->
        <div class="chat-header" v-if="currentConversation">
          <div class="header-info">
            <el-avatar :size="40" :src="getAvatarUrl(currentConversation.otherUser.avatar)">
              {{ currentConversation.otherUser.companyName?.substring(0, 1) || currentConversation.otherUser.username?.substring(0, 1) || '企' }}
            </el-avatar>
            <div class="user-info">
              <div class="user-name">{{ currentConversation.otherUser.companyName || currentConversation.otherUser.username }}</div>
              <div class="user-status">{{ getOnlineStatus(currentConversation) }}</div>
            </div>
          </div>
          <div class="header-actions">
            <el-dropdown trigger="click" @command="handleMoreAction">
              <el-button text>
                <el-icon><More /></el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="pin">
                    {{ currentConversation.isPinned ? '取消置顶' : '置顶会话' }}
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>删除会话</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>

        <!-- 聊天消息区域 -->
        <div class="chat-messages" ref="messagesContainer">
          <el-empty v-if="!currentConversation" description="请选择会话开始聊天" :image-size="150" />
          
          <div v-else class="messages-wrapper">
            <!-- 加载更多 -->
            <div class="load-more" v-if="hasMoreMessages">
              <el-button text size="small" @click="loadMoreMessages" :loading="loadingMessages">
                加载更多消息
              </el-button>
            </div>

            <!-- 消息列表 -->
            <div v-for="msg in messages" :key="msg._id" :class="['message-item', { 'is-mine': isMineMessage(msg) }]">
              <el-avatar :size="40" :src="getAvatarUrl(msg.senderRole === 0 ? null : currentConversation.otherUser.avatar)">
                {{ msg.senderRole === 0 ? (store.state.userInfo?.name?.substring(0, 1) || '我') : (currentConversation.otherUser.companyName?.substring(0, 1) || '企') }}
              </el-avatar>
              
              <div class="message-content">
                <div class="message-time">
                  {{ formatMessageTime(msg.createdAt) }}
                  <span v-if="msg.isRead && isMineMessage(msg)" class="read-status">已读</span>
                </div>
                
                <div v-if="msg.isRecalled" class="message-bubble recalled">
                  消息已撤回
                </div>
                <div v-else :class="['message-bubble', msg.messageType]">
                  <!-- 文本消息 -->
                  <template v-if="msg.messageType === 'text'">
                    {{ msg.content }}
                  </template>
                  
                  <!-- 图片消息 -->
                  <template v-if="msg.messageType === 'image'">
                    <el-image :src="msg.content" :preview-src-list="[msg.content]" fit="cover" style="max-width: 200px; border-radius: 4px;" />
                  </template>
                  
                  <!-- 文件消息 -->
                  <template v-if="msg.messageType === 'file'">
                    <div class="file-message">
                      <el-icon :size="24"><Document /></el-icon>
                      <div class="file-info">
                        <div class="file-name">{{ msg.fileName }}</div>
                        <div class="file-size">{{ formatFileSize(msg.fileSize) }}</div>
                      </div>
                      <el-button text type="primary" @click="downloadFile(msg)">下载</el-button>
                    </div>
                  </template>
                </div>
                
                <!-- 撤回按钮 -->
                <div v-if="!msg.isRecalled && isMineMessage(msg) && canRecall(msg)" class="message-actions">
                  <el-button text size="small" @click="recallMessage(msg)">撤回</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 聊天输入区域 -->
        <div class="chat-input" v-if="currentConversation">
          <!-- 工具栏 -->
          <div class="input-toolbar">
            <el-upload
              :action="uploadUrl"
              :headers="uploadHeaders"
              :before-upload="beforeUpload"
              :on-success="handleUploadSuccess"
              :show-file-list="false"
              accept="image/*"
            >
              <el-button text>
                <el-icon><Picture /></el-icon>
                图片
              </el-button>
            </el-upload>
            <el-upload
              :action="uploadUrl"
              :headers="uploadHeaders"
              :before-upload="beforeFileUpload"
              :on-success="handleFileUploadSuccess"
              :show-file-list="false"
            >
              <el-button text>
                <el-icon><Folder /></el-icon>
                文件
              </el-button>
            </el-upload>
          </div>

          <!-- 输入框 -->
          <div class="input-area">
            <el-input
              v-model="messageInput"
              type="textarea"
              :rows="3"
              placeholder="输入消息... (Ctrl+Enter发送)"
              @keydown.ctrl.enter="sendMessage"
            />
            <el-button type="primary" @click="sendMessage" :disabled="!messageInput.trim()">
              发送
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@/util/axios.config'
import store from '@/store'
import { initSocket, getSocket, disconnectSocket, isConnected, onMessage, emit } from '@/util/socket'
import { 
  ChatDotRound, Search, More, Picture, Folder, Document, Loading
} from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'

const route = useRoute()
const router = useRouter()

// 会话相关
const conversations = ref([])
const currentConversation = ref(null)
const conversationSearch = ref('')
const loadingConversations = ref(false)
const totalUnreadCount = ref(0)

// 消息相关
const messages = ref([])
const messageInput = ref('')
const loadingMessages = ref(false)
const hasMoreMessages = ref(false)
const messagesContainer = ref(null)

// 上传配置
const uploadUrl = computed(() => {
  const baseURL = axios.defaults.baseURL || 'http://jxnujwc.top:30000'
  return `${baseURL}/staffapi/chat/upload`
})
const uploadHeaders = computed(() => ({
  Authorization: localStorage.getItem('token')
}))

// 获取头像URL
const getAvatarUrl = (url) => {
  if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
    return url
  }
  return ''
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return ''
  const now = new Date()
  const msgTime = new Date(time)
  const diff = now - msgTime
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 7) return `${days}天前`
  
  const pad = (n) => n.toString().padStart(2, '0')
  return `${msgTime.getFullYear()}-${pad(msgTime.getMonth() + 1)}-${pad(msgTime.getDate())}`
}

const formatMessageTime = (time) => {
  if (!time) return ''
  const d = new Date(time)
  const pad = (n) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// 格式化最后一条消息
const formatLastMessage = (lastMessage) => {
  if (!lastMessage) return '暂无消息'
  if (lastMessage.messageType === 'image') return '[图片]'
  if (lastMessage.messageType === 'file') return '[文件]'
  return lastMessage.content || ''
}

// 格式化文件大小
const formatFileSize = (size) => {
  if (!size) return '0B'
  const units = ['B', 'KB', 'MB', 'GB']
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(2)} ${units[unitIndex]}`
}

// 判断是否是自己的消息
const isMineMessage = (msg) => {
  return msg.senderRole === 0
}

// 判断消息是否可以撤回（2分钟内）
const canRecall = (msg) => {
  const now = new Date()
  const msgTime = new Date(msg.createdAt)
  const diff = (now - msgTime) / 1000 / 60
  return diff <= 2 && isMineMessage(msg)
}

// 获取在线状态
const getOnlineStatus = (conversation) => {
  if (!conversation.lastActiveAt || !conversation.lastActiveAt.company) return '离线'
  const lastActive = new Date(conversation.lastActiveAt.company)
  const now = new Date()
  const diff = (now - lastActive) / 1000 / 60
  if (diff < 5) return '在线'
  return '离线'
}

// 加载会话列表
const loadConversations = async () => {
  loadingConversations.value = true
  try {
    const { data } = await axios.get('/staffapi/chat/conversations', {
      params: {
        keyword: conversationSearch.value,
        pageSize: 50
      }
    })
    
    if (data.ActionType === 'ok') {
      conversations.value = data.list || []
      console.log('[Chat] 加载会话列表成功，数量:', conversations.value.length)
    } else {
      console.error('[Chat] 加载会话列表失败，响应:', data)
      ElMessage.error(data.msg || '加载会话列表失败')
      conversations.value = []
    }
  } catch (error) {
    console.error('[Chat] 加载会话列表异常:', error)
    ElMessage.error(error.response?.data?.msg || error.message || '加载会话列表失败')
    conversations.value = []
  } finally {
    loadingConversations.value = false
  }
}

// 加载未读消息数
const loadUnreadCount = async () => {
  try {
    const { data } = await axios.get('/staffapi/chat/unread-count')
    if (data.ActionType === 'ok') {
      totalUnreadCount.value = data.totalUnread || 0
    }
  } catch (error) {
    console.error('加载未读数失败:', error)
  }
}

// 选择会话
const selectConversation = async (conv) => {
  currentConversation.value = conv
  messages.value = []
  
  if (isConnected()) {
    emit('join-conversation', {
      conversationId: conv.conversationId
    })
  }
  
  await loadMessages()
  await markAsRead()
}

// 加载聊天记录
const loadMessages = async () => {
  if (!currentConversation.value) return
  
  loadingMessages.value = true
  try {
    const { data } = await axios.get('/staffapi/chat/messages', {
      params: {
        conversationId: currentConversation.value.conversationId,
        pageSize: 30
      }
    })
    
    if (data.ActionType === 'ok') {
      messages.value = data.list || []
      hasMoreMessages.value = data.hasMore || false
      
      await nextTick()
      scrollToBottom()
    }
  } catch (error) {
    console.error('加载消息失败:', error)
    ElMessage.error('加载消息失败')
  } finally {
    loadingMessages.value = false
  }
}

// 加载更多消息
const loadMoreMessages = async () => {
  if (!currentConversation.value || !hasMoreMessages.value || loadingMessages.value) return
  
  loadingMessages.value = true
  try {
    const firstMessage = messages.value[0]
    const { data } = await axios.get('/staffapi/chat/messages', {
      params: {
        conversationId: currentConversation.value.conversationId,
        beforeMessageId: firstMessage?._id,
        pageSize: 30
      }
    })
    
    if (data.ActionType === 'ok') {
      const newMessages = data.list || []
      messages.value = [...newMessages, ...messages.value]
      hasMoreMessages.value = data.hasMore || false
    }
  } catch (error) {
    console.error('加载更多消息失败:', error)
    ElMessage.error('加载更多消息失败')
  } finally {
    loadingMessages.value = false
  }
}

// 标记已读
const markAsRead = async () => {
  if (!currentConversation.value) return
  
  try {
    if (isConnected()) {
      emit('mark-read', {
        conversationId: currentConversation.value.conversationId
      })
    } else {
      await axios.post('/staffapi/chat/message/read', {
        conversationId: currentConversation.value.conversationId
      })
    }
    
    currentConversation.value.unreadCount = 0
    await loadUnreadCount()
    // 同步更新全局未读消息数
    if (store && store.commit) {
      store.commit('setUnreadMessageCount', totalUnreadCount.value)
    }
  } catch (error) {
    console.error('标记已读失败:', error)
  }
}

// 发送消息
const sendMessage = async () => {
  const content = messageInput.value.trim()
  if (!content || !currentConversation.value) return
  
  try {
    if (isConnected()) {
      emit('send-message', {
        conversationId: currentConversation.value.conversationId,
        receiverId: currentConversation.value.otherUser.username,
        messageType: 'text',
        content
      })
      messageInput.value = ''
    } else {
      await sendMessageViaHTTP()
    }
  } catch (error) {
    console.error('WebSocket 发送消息失败:', error)
    await sendMessageViaHTTP()
  }
}

// 通过 HTTP 发送消息
const sendMessageViaHTTP = async () => {
  const content = messageInput.value.trim()
  if (!content || !currentConversation.value) return
  
  try {
    const { data } = await axios.post('/staffapi/chat/message/send', {
      conversationId: currentConversation.value.conversationId,
      receiverId: currentConversation.value.otherUser.username,
      messageType: 'text',
      content
    })
    
    if (data.ActionType === 'ok') {
      messages.value.push(data.message)
      messageInput.value = ''
      await nextTick()
      scrollToBottom()
      await loadConversations()
    }
  } catch (error) {
    console.error('发送消息失败:', error)
    ElMessage.error('发送消息失败')
  }
}

// 撤回消息
const recallMessage = async (msg) => {
  try {
    await ElMessageBox.confirm('确定要撤回这条消息吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    if (isConnected()) {
      emit('recall-message', {
        messageId: msg._id,
        conversationId: currentConversation.value?.conversationId
      })
    } else {
      await recallMessageViaHTTP(msg)
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤回消息失败:', error)
      ElMessage.error(error.response?.data?.msg || '撤回失败')
    }
  }
}

// 通过 HTTP 撤回消息
const recallMessageViaHTTP = async (msg) => {
  try {
    const { data } = await axios.post('/staffapi/chat/message/recall', {
      messageId: msg._id
    })
    
    if (data.ActionType === 'ok') {
      msg.isRecalled = true
      msg.content = '消息已撤回'
      ElMessage.success('撤回成功')
    }
  } catch (error) {
    console.error('撤回消息失败:', error)
    ElMessage.error(error.response?.data?.msg || '撤回失败')
  }
}

// 滚动到底部
const scrollToBottom = () => {
  if (messagesContainer.value) {
    const el = messagesContainer.value.$el || messagesContainer.value
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }
}

// 更多操作
const handleMoreAction = async (command) => {
  if (!currentConversation.value) return
  
  switch (command) {
    case 'pin':
      await pinConversation()
      break
    case 'delete':
      await deleteConversation()
      break
  }
}

// 置顶会话
const pinConversation = async () => {
  try {
    const isPinned = !currentConversation.value.isPinned
    const { data } = await axios.post('/staffapi/chat/conversation/pin', {
      conversationId: currentConversation.value.conversationId,
      isPinned
    })
    
    if (data.ActionType === 'ok') {
      currentConversation.value.isPinned = isPinned
      await loadConversations()
      ElMessage.success(isPinned ? '置顶成功' : '取消置顶成功')
    }
  } catch (error) {
    console.error('置顶操作失败:', error)
    ElMessage.error('操作失败')
  }
}

// 删除会话
const deleteConversation = async () => {
  try {
    await ElMessageBox.confirm('确定要删除这个会话吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const { data } = await axios.post('/staffapi/chat/conversation/delete', {
      conversationId: currentConversation.value.conversationId
    })
    
    if (data.ActionType === 'ok') {
      currentConversation.value = null
      await loadConversations()
      ElMessage.success('删除成功')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除会话失败:', error)
      ElMessage.error('删除失败')
    }
  }
}

// 上传前验证
const beforeUpload = (file) => {
  const isImage = file.type.startsWith('image/')
  const isLt10M = file.size / 1024 / 1024 < 10
  
  if (!isImage) {
    ElMessage.error('只能上传图片文件!')
    return false
  }
  if (!isLt10M) {
    ElMessage.error('图片大小不能超过 10MB!')
    return false
  }
  return true
}

const beforeFileUpload = (file) => {
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isLt10M) {
    ElMessage.error('文件大小不能超过 10MB!')
    return false
  }
  return true
}

// 上传成功
const handleUploadSuccess = async (response) => {
  if (response.ActionType === 'ok') {
    if (isConnected()) {
      emit('send-message', {
        conversationId: currentConversation.value.conversationId,
        receiverId: currentConversation.value.otherUser.username,
        messageType: 'image',
        content: response.fileUrl
      })
    } else {
      try {
        const { data } = await axios.post('/staffapi/chat/message/send', {
          conversationId: currentConversation.value.conversationId,
          receiverId: currentConversation.value.otherUser.username,
          messageType: 'image',
          content: response.fileUrl
        })
        
        if (data.ActionType === 'ok') {
          messages.value.push(data.message)
          await nextTick()
          scrollToBottom()
          await loadConversations()
        }
      } catch (error) {
        console.error('发送图片失败:', error)
        ElMessage.error('发送图片失败')
      }
    }
  }
}

const handleFileUploadSuccess = async (response) => {
  if (response.ActionType === 'ok') {
    if (isConnected()) {
      emit('send-message', {
        conversationId: currentConversation.value.conversationId,
        receiverId: currentConversation.value.otherUser.username,
        messageType: 'file',
        content: response.fileUrl,
        fileName: response.fileName,
        fileSize: response.fileSize
      })
    } else {
      try {
        const { data } = await axios.post('/staffapi/chat/message/send', {
          conversationId: currentConversation.value.conversationId,
          receiverId: currentConversation.value.otherUser.username,
          messageType: 'file',
          content: response.fileUrl,
          fileName: response.fileName,
          fileSize: response.fileSize
        })
        
        if (data.ActionType === 'ok') {
          messages.value.push(data.message)
          await nextTick()
          scrollToBottom()
          await loadConversations()
        }
      } catch (error) {
        console.error('发送文件失败:', error)
        ElMessage.error('发送文件失败')
      }
    }
  }
}

// 下载文件
const downloadFile = (msg) => {
  const a = document.createElement('a')
  a.href = msg.content
  a.download = msg.fileName || 'file'
  a.click()
}

// WebSocket 初始化
const initWebSocket = () => {
  const token = localStorage.getItem('token')
  if (!token) {
    console.warn('[WebSocket] 未找到 token')
    return
  }

  console.log('[WebSocket] 初始化连接...')
  initSocket(token)
  
  onMessage('connected', () => {
    console.log('[WebSocket] 连接成功')
    emit('update-online-status', {})
  })

  onMessage('new-message', (data) => {
    const { conversationId, message } = data
    
    if (currentConversation.value && currentConversation.value.conversationId === conversationId) {
      const exists = messages.value.some(m => m._id === message._id)
      if (!exists) {
        messages.value.push(message)
        nextTick(() => scrollToBottom())
        markAsRead()
      }
    }
    
    loadConversations()
    loadUnreadCount()
  })

  onMessage('message-sent', (data) => {
    const { conversationId, message } = data
    
    if (currentConversation.value && currentConversation.value.conversationId === conversationId) {
      const exists = messages.value.some(m => m._id === message._id)
      if (!exists) {
        messages.value.push(message)
        nextTick(() => scrollToBottom())
      }
    }
    
    loadConversations()
  })

  onMessage('message-recalled', (data) => {
    const { messageId, conversationId } = data
    
    if (currentConversation.value && currentConversation.value.conversationId === conversationId) {
      const msg = messages.value.find(m => m._id === messageId)
      if (msg) {
        msg.isRecalled = true
        msg.content = '消息已撤回'
      }
    }
    
    loadConversations()
  })

  onMessage('disconnect', () => {
    console.log('[WebSocket] 连接断开，启动轮询')
    startPolling()
  })

  onMessage('connect_error', (error) => {
    console.error('[WebSocket] 连接错误:', error)
    startPolling()
  })
  
  // 监听 connect 事件（WebSocket 连接成功时触发）
  onMessage('connect', () => {
    console.log('[WebSocket] 已连接，停止轮询')
    stopPolling()
  })
}

// 轮询机制（WebSocket 失败时的降级方案）
let pollingTimer = null
const startPolling = () => {
  // 如果 WebSocket 已连接，不需要轮询
  if (isConnected()) {
    console.log('[轮询] WebSocket 已连接，跳过轮询')
    return
  }
  
  // 清除之前的定时器
  if (pollingTimer) {
    clearInterval(pollingTimer)
  }
  
  console.log('[轮询] 启动消息轮询（每秒）')
  
  // 每秒轮询一次新消息
  pollingTimer = setInterval(async () => {
      // 刷新会话列表和未读数
      try {
        await loadConversations()
        await loadUnreadCount()
        // 同步更新全局未读消息数
        if (store && store.commit) {
          store.commit('setUnreadMessageCount', totalUnreadCount.value)
        }
        
        // 如果当前有选中的会话，也刷新消息
      if (currentConversation.value) {
        const lastMessage = messages.value[messages.value.length - 1]
        const lastMessageId = lastMessage?._id
        
        try {
          const { data } = await axios.get('/staffapi/chat/messages', {
            params: {
              conversationId: currentConversation.value.conversationId,
              afterMessageId: lastMessageId,
              pageSize: 50
            }
          })
          
          if (data.ActionType === 'ok' && data.list && data.list.length > 0) {
            // 添加新消息
            const newMessages = data.list.filter(msg => {
              return !messages.value.some(m => m._id === msg._id)
            })
            
            if (newMessages.length > 0) {
              console.log('[轮询] 收到新消息:', newMessages.length, '条')
              messages.value.push(...newMessages)
              await nextTick()
              scrollToBottom()
              await markAsRead()
            }
          }
        } catch (error) {
          console.error('[轮询] 获取消息失败:', error)
        }
      }
    } catch (error) {
      console.error('[轮询] 刷新失败:', error)
    }
  }, 1000) // 每秒轮询一次
}

const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
    console.log('[轮询] 停止消息轮询')
  }
}

onMounted(async () => {
  console.log('[Chat] 组件挂载开始')
  
  try {
    initWebSocket()
    await loadConversations()
    await loadUnreadCount()
    
    // 同步更新全局未读消息数
    if (store && store.commit) {
      store.commit('setUnreadMessageCount', totalUnreadCount.value)
    }
    
    // 延迟启动轮询，给 WebSocket 一些时间连接
    setTimeout(() => {
      if (!isConnected()) {
        console.log('[Chat] WebSocket 未连接，启动轮询机制')
        startPolling()
      } else {
        console.log('[Chat] WebSocket 已连接，无需轮询')
      }
    }, 5000) // 5秒后检查
    
    // 检查是否有企业用户名参数
    const companyUsername = route.query.companyUsername
    if (companyUsername) {
      console.log('[Chat] 检测到企业用户名:', companyUsername)
      
      await new Promise(resolve => setTimeout(resolve, 500))
      
      const existingConv = conversations.value.find(
        conv => conv.otherUser.username === companyUsername
      )
      
      if (existingConv) {
        await selectConversation(existingConv)
      } else {
        try {
          const { data } = await axios.post('/staffapi/chat/conversation/create', {
            companyUsername
          })
          
          if (data.ActionType === 'ok' && data.conversation) {
            await loadConversations()
            await new Promise(resolve => setTimeout(resolve, 300))
            
            const newConv = conversations.value.find(
              c => c.conversationId === data.conversation.conversationId
            )
            if (newConv) {
              await selectConversation(newConv)
            }
          }
        } catch (error) {
          console.error('[Chat] 创建会话失败:', error)
          ElMessage.error('创建会话失败')
        }
      }
      
      router.replace({ path: '/chat', query: {} })
    }
  } catch (error) {
    console.error('[Chat] 初始化失败:', error)
    ElMessage.error('页面初始化失败')
  }
  
  console.log('[Chat] 组件挂载完成')
})

onUnmounted(() => {
  // 停止轮询
  stopPolling()
})
</script>

<style scoped lang="scss">
.chat-page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background: #f5f7fa;
  padding: 0;
  margin: 0;
  z-index: 1000;
  
  .page-header {
    padding: 16px 20px;
    background: #fff;
    border-bottom: 1px solid #e4e7ed;
    
    h2 {
      margin: 0 0 8px 0;
      font-size: 20px;
      color: #303133;
    }
    
    p {
      margin: 0;
      font-size: 14px;
      color: #909399;
    }
  }
  
  .chat-layout {
    display: flex;
    height: calc(100vh - 80px);
    width: 100%;
  }
}

// 左侧会话列表
.conversations-sidebar {
  width: 320px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  
  .sidebar-header {
    padding: 16px;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    h3 {
      margin: 0;
      font-size: 18px;
    }
  }
  
  .search-box {
    padding: 12px 16px;
    border-bottom: 1px solid #e4e7ed;
  }
  
  .conversations-list {
    flex: 1;
    overflow-y: auto;
    
    .loading-state {
      padding: 20px;
      text-align: center;
      color: #909399;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    
    .conversation-item {
      padding: 12px 16px;
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
      transition: background 0.3s;
      border-bottom: 1px solid #f5f7fa;
      
      &:hover {
        background: #f5f7fa;
      }
      
      &.active {
        background: #ecf5ff;
      }
      
      .conv-info {
        flex: 1;
        min-width: 0;
        
        .conv-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
          
          .conv-name {
            font-weight: 600;
            font-size: 14px;
            color: #303133;
          }
          
          .conv-time {
            font-size: 12px;
            color: #909399;
          }
        }
        
        .conv-last-message {
          font-size: 13px;
          color: #909399;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }
}

// 右侧聊天区域
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  
  .chat-header {
    padding: 16px 20px;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .header-info {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .user-info {
        .user-name {
          font-weight: 600;
          font-size: 16px;
          color: #303133;
        }
        
        .user-status {
          font-size: 12px;
          color: #909399;
        }
      }
    }
  }
  
  .chat-messages {
    flex: 1;
    background: #f5f7fa;
    padding: 20px;
    overflow-y: auto;
    overflow-x: hidden;
    
    .messages-wrapper {
      .load-more {
        text-align: center;
        margin-bottom: 16px;
      }
      
      .message-item {
        display: flex;
        gap: 12px;
        margin-bottom: 20px;
        
        &.is-mine {
          flex-direction: row-reverse;
          
          .message-content {
            align-items: flex-end;
            
            .message-bubble {
              background: #409eff;
              color: #fff;
            }
          }
        }
        
        .message-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          max-width: 60%;
          
          .message-time {
            font-size: 12px;
            color: #909399;
            
            .read-status {
              margin-left: 8px;
              color: #67c23a;
            }
          }
          
          .message-bubble {
            background: #fff;
            padding: 10px 14px;
            border-radius: 8px;
            word-break: break-word;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            
            &.recalled {
              background: #f5f7fa;
              color: #909399;
              font-style: italic;
            }
            
            .file-message {
              display: flex;
              align-items: center;
              gap: 8px;
              
              .file-info {
                flex: 1;
                
                .file-name {
                  font-size: 14px;
                  color: #303133;
                }
                
                .file-size {
                  font-size: 12px;
                  color: #909399;
                }
              }
            }
          }
          
          .message-actions {
            margin-top: 4px;
          }
        }
      }
    }
  }
  
  .chat-input {
    padding: 12px 20px;
    border-top: 1px solid #e4e7ed;
    background: #fff;
    
    .input-toolbar {
      display: flex;
      gap: 8px;
      margin-bottom: 8px;
    }
    
    .input-area {
      display: flex;
      gap: 12px;
      align-items: flex-end;
    }
  }
}
</style>
